import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import { ResumeUploadModal } from "../components/ResumeUploadModal";
import ATSDataViewer from "../components/ATSDataViewer";
import { ResumeAnalysisResults } from "../components/ResumeAnalysisResults";
import { useResumes } from "../hooks/useResumes";
import { useSubscriptionContext } from "../contexts/SubscriptionContext";
import { apiService } from "../services/apiService";
import toast from "react-hot-toast";
import { useMessageBox } from "../components/MessageBox";
import { ResumeAnalysis } from "../../../shared/types";
import {
  ResumeAtsIcon,
  ResumeAnalyzeIcon,
  ResumeDeleteIcon,
  ResumeReprocessIcon,
  ResumeViewIcon,
} from "../components/ResumeActionIcons";
import { PopupTooltip } from "../components/PopupTooltip";
// import { ResumeItem } from '../services/apiService'
import "./ResumesPage.css";

// Helper function to transform analysis data from snake_case to camelCase
function transformAnalysisToCamelCase(analysis: any) {
  return {
    ...analysis,
    id:
      analysis.id ||
      analysis.analysis_id ||
      analysis.analysisId ||
      analysis.resume_id ||
      analysis.resumeId,
    analysisStatus: analysis.analysis_status || analysis.analysisStatus,
    atsErrors: analysis.ats_errors || analysis.atsErrors || [],
    dataValidationErrors:
      analysis.data_validation_errors || analysis.dataValidationErrors || [],
    discrepancies: analysis.discrepancies || [],
    employmentGaps: analysis.employment_gaps || analysis.employmentGaps || [],
    jobHoppingPatterns:
      analysis.job_hopping_patterns || analysis.jobHoppingPatterns || [],
    grammarErrors: analysis.grammar_errors || analysis.grammarErrors || [],
    professionalImprovements:
      analysis.professional_improvements ||
      analysis.professionalImprovements ||
      [],
    fieldSpecificSuggestions:
      analysis.field_specific_suggestions ||
      analysis.fieldSpecificSuggestions ||
      [],
    contextualImprovements:
      analysis.contextual_improvements || analysis.contextualImprovements || [],
    otherSuggestions:
      analysis.other_suggestions || analysis.otherSuggestions || [],
    rewriteOptimizationSuggestions:
      analysis.rewrite_optimization_suggestions ||
      analysis.rewriteOptimizationSuggestions ||
      [],
    improvedContent:
      analysis.improved_resume_content ||
      analysis.improvedContent ||
      analysis.improvedResumeContent ||
      {},
    improvedResumeContent:
      analysis.improved_resume_content || analysis.improvedResumeContent || {},
    resumeId: analysis.resume_id || analysis.resumeId,
    createdAt: analysis.created_at || analysis.createdAt,
    updatedAt: analysis.updated_at || analysis.updatedAt,
  };
}

function isDocxResume(resume: any): boolean {
  const filename = (
    resume?.originalFilename ||
    resume?.filename ||
    ""
  ).toLowerCase();
  const fileType = (resume?.fileType || resume?.file_type || "").toLowerCase();
  return (
    filename.endsWith(".docx") ||
    fileType ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  );
}

function getResumeGenerationSourceFormat(
  resume: any,
): "docx" | "pdf" | "other" {
  const filename = (
    resume?.originalFilename ||
    resume?.filename ||
    ""
  ).toLowerCase();
  const fileType = (resume?.fileType || resume?.file_type || "").toLowerCase();

  if (isDocxResume(resume)) {
    return "docx";
  }
  if (filename.endsWith(".pdf") || fileType === "application/pdf") {
    return "pdf";
  }
  return "other";
}

function getResumeFileSize(resume: any): number {
  return Number(resume?.fileSize || resume?.file_size || 0);
}

function getResumeStatusValue(resume: any): string {
  return String(resume?.status || "pending");
}

function getResumeProcessingStatusValue(resume: any): string {
  return String(resume?.atsProcessingStatus || resume?.status || "pending");
}

function isGeneratedResume(resume: any): boolean {
  return Boolean(
    resume?.source_resume_id ||
      resume?.sourceResumeId ||
      resume?.generation_mode ||
      resume?.generationMode,
  );
}

function isGeneratedResumeBusy(resume: any): boolean {
  if (!isGeneratedResume(resume)) {
    return false;
  }

  const fileSize = getResumeFileSize(resume);
  const status = getResumeStatusValue(resume);
  const processingStatus = getResumeProcessingStatusValue(resume);

  if (status === "processing" && fileSize <= 0) {
    return true;
  }

  return (
    processingStatus === "processing" ||
    processingStatus === "pending" ||
    processingStatus === "uploaded" ||
    processingStatus === "in_progress"
  );
}

function getGeneratedResumeBusyMessage(resume: any): string {
  const fileSize = getResumeFileSize(resume);
  const status = getResumeStatusValue(resume);

  if (status === "processing" && fileSize <= 0) {
    return "Resume and ATS processing are still running. PDF rewrites usually take about 2-4 minutes.";
  }

  return "Resume and ATS processing are still running. Actions unlock automatically when everything is ready.";
}

export const ResumesPage: React.FC = () => {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteConfirmStep, setDeleteConfirmStep] = useState<1 | 2>(1);
  const [isAtsModalOpen, setIsAtsModalOpen] = useState(false);
  const [selectedAtsResume, setSelectedAtsResume] = useState<any>(null);
  const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
  const [analysis, setAnalysis] = useState<ResumeAnalysis | null>(null);
  const [analysisSourceResume, setAnalysisSourceResume] = useState<any>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isGeneratingResume, setIsGeneratingResume] = useState(false);
  const [isGeneratingResumeAi, setIsGeneratingResumeAi] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [analyzingResumeId, setAnalyzingResumeId] = useState<string | null>(
    null,
  );
  const [reprocessingResumeId, setReprocessingResumeId] = useState<
    string | null
  >(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const resumesHook = useResumes();
  const { resumes, isLoading, isDeleting, deletingResumeId, deleteResume } =
    resumesHook;
  const { reloadSubscription } = useSubscriptionContext();
  const { showMessageBox } = useMessageBox();
  const pageContainerRef = useRef<HTMLDivElement>(null);
  const itemsPerPage = 5;

  const totalPages = useMemo(
    () => Math.ceil(resumes.length / itemsPerPage),
    [resumes.length, itemsPerPage],
  );
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedResumes = useMemo(
    () => resumes.slice(startIndex, endIndex),
    [resumes, startIndex, endIndex],
  );

  useEffect(() => {
    if (totalPages === 0 && currentPage !== 1) {
      setCurrentPage(1);
      return;
    }
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleAddResume = useCallback(() => {
    setIsUploadModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsUploadModalOpen(false);
  }, []);

  const handleResumeClick = useCallback(
    async (e: React.MouseEvent, resume: any) => {
      e.stopPropagation();
      // Download/view the resume file using signed URL
      try {
        const response = await apiService.getResumeDownloadUrl(resume.id);
        // Backend returns: { success: true, downloadUrl: "...", expiresIn: 300 }
        // The downloadUrl is at the top level, not in a data property
        const downloadUrl =
          response.downloadUrl ||
          response.download_url ||
          response.data?.downloadUrl ||
          response.data?.download_url;
        if (response.success && downloadUrl) {
          window.open(downloadUrl, "_blank");
        } else {
          toast.error("Unable to open resume file");
        }
      } catch (error) {
        const status =
          typeof error === "object" && error && "status" in error
            ? Number((error as any).status)
            : undefined;
        if (status === 409) {
          toast.error(
            "Resume is still being generated. PDF rewrites usually take about 2-4 minutes.",
          );
          return;
        }
        toast.error("Unable to open resume file");
      }
    },
    [],
  );

  const handleViewAtsData = useCallback(
    async (e: React.MouseEvent, resume: any) => {
      e.stopPropagation();

      try {
        // Fetch detailed ATS data from the API
        const atsData = await apiService.getAtsData(resume.id);

        // Combine resume info with ATS data
        const resumeWithAtsData = {
          ...resume,
          atsData: atsData,
        };

        setSelectedAtsResume(resumeWithAtsData);
        setIsAtsModalOpen(true);
      } catch (error) {
        // Show error message to user
        toast.error("Failed to load ATS data. Please try again.");

        // Still open modal with basic resume data
        setSelectedAtsResume(resume);
        setIsAtsModalOpen(true);
      }
    },
    [],
  );

  const handleCloseAtsModal = useCallback(() => {
    setIsAtsModalOpen(false);
    setSelectedAtsResume(null);
  }, []);

  const handleAnalyzeResume = useCallback(
    async (e: React.MouseEvent, resume: any) => {
      e.stopPropagation();
      setAnalysisSourceResume(resume);

      try {
        setAnalyzingResumeId(resume.id);
        setIsAnalyzing(true);
        setAnalysisError(null);

        // Check if analysis already exists
        const existingAnalysisResponse = await apiService.getResumeAnalysis(
          resume.id,
        );
        // Backend wraps response in { success: true, data: { ... } }
        const existingAnalysis =
          existingAnalysisResponse?.data || existingAnalysisResponse;
        if (
          existingAnalysis &&
          (existingAnalysis.analysisStatus === "completed" ||
            existingAnalysis.analysis_status === "completed")
        ) {
          // Transform snake_case to camelCase for component compatibility
          const transformedAnalysis =
            transformAnalysisToCamelCase(existingAnalysis);
          // Show existing analysis
          setAnalysis(transformedAnalysis);
          setIsAnalysisModalOpen(true);
          setIsAnalyzing(false);
          setAnalyzingResumeId(null);
          return;
        }

        // Start new analysis
        const response = await apiService.analyzeResume(resume.id);

        if (response.success && response.data) {
          // Backend returns full analysis object immediately (synchronous)
          // Response is already transformed to camelCase by apiService
          // Check if analysis is already completed
          if (
            (response.data.analysisStatus || response.data.analysis_status) ===
            "completed"
          ) {
            // Analysis completed immediately - show results
            setAnalysis(transformAnalysisToCamelCase(response.data));
            setIsAnalysisModalOpen(true);
            setIsAnalyzing(false);
            setAnalyzingResumeId(null);

            // Only reload subscription if tokens were actually charged (not from cache)
            // Handle both snake_case (from backend) and camelCase (from frontend transformation)
            const tokensCharged =
              (response.data.tokensCharged ?? response.data.tokens_charged) !==
                false && !(response.data.fromCache ?? response.data.from_cache);
            if (tokensCharged) {
              toast.success("Resume analysis completed!");
              // Reload subscription to update token balance after analysis completes
              reloadSubscription().catch((error) => {
                console.error(
                  "Failed to reload subscription after resume analysis:",
                  error,
                );
              });
            } else {
              toast.success("Resume analysis retrieved from saved results.");
            }
          } else {
            // Analysis is in progress - poll for results
            toast.success(
              "Resume analysis started. This usually takes about 30-60 seconds.",
            );

            // Poll for results
            const pollInterval = setInterval(async () => {
              try {
                const analysisResultResponse =
                  await apiService.getResumeAnalysis(resume.id);
                // Backend wraps response in { success: true, data: { ... } }
                const analysisResult =
                  analysisResultResponse?.data || analysisResultResponse;
                if (analysisResult) {
                  // Handle both camelCase and snake_case from backend
                  const status =
                    analysisResult.analysisStatus ||
                    analysisResult.analysis_status;
                  if (status === "completed") {
                    clearInterval(pollInterval);
                    // Transform snake_case to camelCase for component compatibility
                    const transformedAnalysis =
                      transformAnalysisToCamelCase(analysisResult);
                    setAnalysis(transformedAnalysis);
                    setIsAnalysisModalOpen(true);
                    setIsAnalyzing(false);
                    setAnalyzingResumeId(null);

                    // Only reload subscription if tokens were actually charged (not from cache)
                    // Handle both snake_case (from backend) and camelCase (from frontend transformation)
                    const tokensCharged =
                      (analysisResult.tokensCharged ??
                        analysisResult.tokens_charged) !== false &&
                      !(analysisResult.fromCache ?? analysisResult.from_cache);
                    if (tokensCharged) {
                      toast.success("Resume analysis completed!");
                      // Reload subscription to update token balance after analysis completes
                      reloadSubscription().catch((error) => {
                        console.error(
                          "Failed to reload subscription after resume analysis:",
                          error,
                        );
                      });
                    } else {
                      toast.success(
                        "Resume analysis retrieved from saved results.",
                      );
                    }
                  } else if (status === "failed") {
                    clearInterval(pollInterval);
                    setAnalysisError("Analysis failed. Please try again.");
                    setIsAnalyzing(false);
                    setAnalyzingResumeId(null);
                    toast.error("Resume analysis failed");
                  }
                  // If still processing, continue polling
                }
              } catch (error) {
                // Silently continue polling on error
              }
            }, 3000); // Poll every 3 seconds

            // Stop polling after 2 minutes
            setTimeout(() => {
              clearInterval(pollInterval);
              if (isAnalyzing) {
                setIsAnalyzing(false);
                setAnalyzingResumeId(null);
                toast.error(
                  "Analysis is taking longer than expected. Please check back later.",
                );
              }
            }, 120000);
          }
        } else {
          throw new Error(response.error || "Failed to start analysis");
        }
      } catch (error: any) {
        setAnalysisError(error.message || "Failed to analyze resume");
        setIsAnalyzing(false);
        setAnalyzingResumeId(null);
        toast.error(error.message || "Failed to analyze resume");
      }
    },
    [showMessageBox, isAnalyzing, reloadSubscription],
  );

  const handleCloseAnalysisModal = useCallback(() => {
    setIsAnalysisModalOpen(false);
    setAnalysis(null);
    setAnalysisSourceResume(null);
    setIsGeneratingResume(false);
    setIsGeneratingResumeAi(false);
    setAnalysisError(null);
  }, []);

  const handleGenerateResumeFromSuggestions = useCallback(
    async (selectedSuggestionIds: string[]) => {
      if (!analysis || !analysisSourceResume) {
        toast.error(
          "Resume analysis source is unavailable. Please reopen the analysis and try again.",
        );
        return;
      }

      if (!selectedSuggestionIds.length) {
        toast.error("Select at least one suggestion to generate a new resume.");
        return;
      }

      const resumeId = analysis.resumeId || analysisSourceResume.id;
      const analysisId =
        analysis.id || analysis.resumeId || analysisSourceResume.id;

      if (!resumeId || !analysisId) {
        toast.error(
          "Resume analysis details are incomplete. Please rerun the analysis and try again.",
        );
        return;
      }

      try {
        setIsGeneratingResume(true);
        const response = await apiService.applyAnalysisSuggestions(
          resumeId,
          analysisId,
          selectedSuggestionIds,
        );

        if (!response.success || !response.data?.newResumeId) {
          throw new Error(
            response.error || "Failed to generate the new resume",
          );
        }

        if (response.data.resume) {
          resumesHook.updateResumes((items) => [
            response.data!.resume!,
            ...items.filter((item) => item.id !== response.data!.newResumeId),
          ]);
        } else {
          await resumesHook.refresh();
        }

        if (response.data.downloadUrl) {
          window.open(response.data.downloadUrl, "_blank");
        }

        if (resumesHook.scheduleStatusPoll) {
          resumesHook.scheduleStatusPoll(response.data.newResumeId, true);
        }

        toast.success(
          response.data.resumeStatus === "processing"
            ? "Resume generation started. PDF rewrites usually take about 2-4 minutes."
            : "Generated a new resume with the selected suggestions.",
        );
      } catch (error: any) {
        toast.error(error.message || "Failed to generate the new resume");
      } finally {
        setIsGeneratingResume(false);
      }
    },
    [analysis, analysisSourceResume, resumesHook],
  );

  const handleGenerateResumeFromSuggestionsAi = useCallback(
    async (selectedSuggestionIds: string[]) => {
      if (!analysis || !analysisSourceResume) {
        toast.error(
          "Resume analysis source is unavailable. Please reopen the analysis and try again.",
        );
        return;
      }

      if (!selectedSuggestionIds.length) {
        toast.error("Select at least one suggestion to run an AI rewrite.");
        return;
      }

      const resumeId = analysis.resumeId || analysisSourceResume.id;
      const analysisId =
        analysis.id || analysis.resumeId || analysisSourceResume.id;

      if (!resumeId || !analysisId) {
        toast.error(
          "Resume analysis details are incomplete. Please rerun the analysis and try again.",
        );
        return;
      }

      try {
        setIsGeneratingResumeAi(true);
        const response = await apiService.applyAnalysisSuggestionsAi(
          resumeId,
          analysisId,
          selectedSuggestionIds,
        );

        if (!response.success || !response.data?.newResumeId) {
          throw new Error(response.error || "Failed to AI rewrite the resume");
        }

        if (response.data.resume) {
          resumesHook.updateResumes((items) => [
            response.data!.resume!,
            ...items.filter((item) => item.id !== response.data!.newResumeId),
          ]);
        } else {
          await resumesHook.refresh();
        }

        if (response.data.downloadUrl) {
          window.open(response.data.downloadUrl, "_blank");
        }

        if (resumesHook.scheduleStatusPoll) {
          resumesHook.scheduleStatusPoll(response.data.newResumeId, true);
        }

        toast.success(
          response.data.resumeStatus === "processing"
            ? "AI rewrite started. PDF rewrites usually take about 2-4 minutes."
            : "AI rewrite completed. A new resume was generated.",
        );
      } catch (error: any) {
        toast.error(error.message || "Failed to AI rewrite the resume");
      } finally {
        setIsGeneratingResumeAi(false);
      }
    },
    [analysis, analysisSourceResume, resumesHook],
  );

  const handleReprocessResume = useCallback(
    async (e: React.MouseEvent, resume: any) => {
      e.stopPropagation();

      try {
        setReprocessingResumeId(resume.id);
        toast.loading("Starting ATS processing...", { id: "reprocess" });

        const response = await apiService.processResume(resume.id);

        if (response.success) {
          toast.success(
            "ATS processing started. This usually takes about 30-60 seconds.",
            { id: "reprocess" },
          );

          // Update resume status to "processing" immediately for UI feedback
          if (resumesHook.updateResumes) {
            resumesHook.updateResumes((items) =>
              items.map((item) =>
                item.id === resume.id
                  ? {
                      ...item,
                      atsProcessingStatus: "processing",
                      status: "processing",
                    }
                  : item,
              ),
            );
          }

          // Start polling for status updates
          if (resumesHook.scheduleStatusPoll) {
            resumesHook.scheduleStatusPoll(resume.id, true); // Start with initial delay
          }

          // Refresh resumes list after a short delay to get latest status from backend
          setTimeout(() => {
            resumesHook.refresh();
          }, 2000);
        } else {
          toast.error(response.error || "Failed to start processing", {
            id: "reprocess",
          });
        }
      } catch (error: any) {
        toast.error(error.message || "Failed to process resume", {
          id: "reprocess",
        });
      } finally {
        setReprocessingResumeId(null);
      }
    },
    [resumesHook],
  );

  const handleDeleteClick = useCallback(
    (e: React.MouseEvent, resumeId: string) => {
      e.stopPropagation();
      setDeleteConfirmId(resumeId);
      setDeleteConfirmStep(1);
    },
    [],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteConfirmId) return;
    const isLastResume = resumes.length === 1;
    if (deleteConfirmStep === 1 && isLastResume) {
      setDeleteConfirmStep(2);
      return;
    }
    await deleteResume(deleteConfirmId);
    setDeleteConfirmId(null);
    setDeleteConfirmStep(1);
  }, [deleteConfirmId, deleteConfirmStep, deleteResume, resumes.length]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmId(null);
    setDeleteConfirmStep(1);
  }, []);

  const handleDeleteGoBack = useCallback(() => {
    setDeleteConfirmStep(1);
  }, []);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading resumes...</p>
        </div>
      );
    }

    if (resumes.length === 0) {
      return (
        <div className="empty-state">
          <div className="empty-icon">
            <div className="icon-container">
              <span className="main-icon">📄</span>
              <div className="icon-glow"></div>
            </div>
          </div>
          <div className="empty-content">
            <h4 className="empty-title">No Resumes Yet</h4>
            <p className="empty-description">
              Upload your first resume to get started with resume management and
              optimization.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="resumes-list">
        <div className="resumes-list-toolbar">
          <div className="resumes-count-info">
            {totalPages > 1 ? (
              <span>
                Showing {startIndex + 1}-{Math.min(endIndex, resumes.length)} of{" "}
                {resumes.length} resumes
              </span>
            ) : (
              <span>
                Showing {resumes.length} of {resumes.length} resumes
              </span>
            )}
          </div>
          {totalPages > 1 && (
            <div className="pagination-container">
              <div className="pagination-controls">
                <button
                  className="pagination-btn"
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={currentPage === 1}
                  title="Previous page"
                >
                  ← Previous
                </button>
                <div className="pagination-numbers">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => {
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            className={`pagination-number ${currentPage === page ? "active" : ""}`}
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </button>
                        );
                      } else if (
                        page === currentPage - 2 ||
                        page === currentPage + 2
                      ) {
                        return (
                          <span key={page} className="pagination-ellipsis">
                            ...
                          </span>
                        );
                      }
                      return null;
                    },
                  )}
                </div>
                <button
                  className="pagination-btn"
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                  }
                  disabled={currentPage === totalPages}
                  title="Next page"
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="resumes-grid">
          {paginatedResumes.map((resume) => {
            const resumeFileSize = getResumeFileSize(resume);
            const resumeStatus = getResumeStatusValue(resume);
            const processingStatus = getResumeProcessingStatusValue(resume);
            const generatedResumeBusy = isGeneratedResumeBusy(resume);
            const generatedResumeBusyMessage = generatedResumeBusy
              ? getGeneratedResumeBusyMessage(resume)
              : "";

            const guardBusyResumeAction = (e: React.MouseEvent) => {
              e.stopPropagation();
              if (generatedResumeBusy) {
                toast.error(generatedResumeBusyMessage);
                return true;
              }
              return false;
            };

            return (
              <div
                key={resume.id}
                className={`resume-card ${generatedResumeBusy ? "resume-card-busy" : ""}`}
                title="Resume management"
              >
                <div className="resume-card-header">
                  <div className="resume-icon">
                    <span className="icon">📄</span>
                  </div>
                  <div className="resume-info">
                    <h3 className="resume-name">
                      {resume.originalFilename ||
                        resume.filename ||
                        resume.fileName ||
                        "Resume"}
                    </h3>
                    <p className="resume-type">
                      {resume.resumeType || "General"}
                    </p>
                    <div className="resume-meta">
                      <div className="resume-meta-item">
                        <span className="resume-meta-icon">📅</span>
                        <span>
                          {resume.uploadedAt
                            ? new Date(resume.uploadedAt).toLocaleDateString()
                            : "Unknown"}
                        </span>
                      </div>
                      <div className="resume-meta-item">
                        <span className="resume-meta-icon">📁</span>
                        <span>
                          {resume.fileSize
                            ? `${(resume.fileSize / 1024).toFixed(1)} KB`
                            : "Unknown"}
                        </span>
                      </div>
                      {resume.atsScore !== undefined &&
                        resume.atsScore !== null && (
                          <div className="resume-meta-item">
                            <span className="resume-meta-icon">⭐</span>
                            <span>{resume.atsScore}%</span>
                          </div>
                        )}
                    </div>
                  </div>
                  <div className="resume-actions">
                    <div className="resume-status">
                      <span
                        className={`status-badge ${generatedResumeBusy ? "processing" : processingStatus}`}
                      >
                        {(() => {
                          if (
                            resumeStatus === "processing" &&
                            resumeFileSize <= 0
                          ) {
                            return "Generating Resume";
                          }
                          if (generatedResumeBusy) return "Finalizing ATS";
                          const status = processingStatus;
                          if (status === "processing") return "ATS Processing";
                          if (status === "processed" || status === "completed")
                            return "ATS Completed";
                          if (status === "uploaded") return "Uploaded";
                          if (status === "error") return "Processing Error";
                          if (status === "failed") return "Processing Failed";
                          if (status === "timeout") return "Processing Timeout";
                          return "Pending";
                        })()}
                      </span>
                      {processingStatus === "processing" && (
                        <span
                          className="ats-processing-icon"
                          title="ATS processing in progress"
                        >
                          ⏳
                        </span>
                      )}
                      {processingStatus === "error" && (
                        <span
                          className="ats-error-icon"
                          title="ATS processing error"
                        >
                          ⚠️
                        </span>
                      )}
                      {processingStatus === "processed" ||
                      processingStatus === "completed" ? (
                        <span
                          className="ats-score-display"
                          title={`Compliance: ${resume.atsComplianceScorePct != null ? resume.atsComplianceScorePct : resume.atsScore || 0}%`}
                        >
                          {resume.atsComplianceScorePct != null
                            ? resume.atsComplianceScorePct
                            : resume.atsScore || 0}
                          %
                        </span>
                      ) : null}
                    </div>
                    <div className="action-buttons">
                      <PopupTooltip
                        content={
                          generatedResumeBusy
                            ? generatedResumeBusyMessage
                            : "Download or open this resume file in a new tab"
                        }
                        placement="top"
                      >
                        <button
                          className={`action-button view-file-button ${generatedResumeBusy ? "is-disabled" : ""}`}
                          onClick={(e) => {
                            if (guardBusyResumeAction(e)) return;
                            handleResumeClick(e, resume);
                          }}
                          title="View / download resume"
                          data-tour-id="resume-view-file"
                          aria-disabled={generatedResumeBusy}
                          aria-label="View or download resume file"
                        >
                          <ResumeViewIcon className="resume-action-icon" />
                        </button>
                      </PopupTooltip>
                      {(resume.atsProcessingStatus === "processed" ||
                        resume.status === "processed") && (
                        <PopupTooltip
                          content="View ATS (Applicant Tracking System) parsing results for this resume"
                          placement="top"
                        >
                          <button
                            className="action-button view-ats-button"
                            onClick={(e) => handleViewAtsData(e, resume)}
                            title="View ATS data"
                            data-tour-id="resume-view-ats"
                            aria-label="View ATS analysis"
                          >
                            <ResumeAtsIcon className="resume-action-icon" />
                          </button>
                        </PopupTooltip>
                      )}
                      <PopupTooltip
                        content={
                          generatedResumeBusy
                            ? generatedResumeBusyMessage
                            : "Run AI-powered analysis for grammar, style, and improvement suggestions"
                        }
                        placement="top"
                      >
                        <button
                          className={`action-button analyze-button ${analyzingResumeId === resume.id ? "analyzing" : ""} ${generatedResumeBusy ? "is-disabled" : ""}`}
                          onClick={(e) => {
                            if (guardBusyResumeAction(e)) return;
                            handleAnalyzeResume(e, resume);
                          }}
                          title="Analyze resume"
                          data-tour-id="resume-analyze"
                          aria-disabled={
                            generatedResumeBusy ||
                            (isAnalyzing && analyzingResumeId === resume.id)
                          }
                          disabled={
                            isAnalyzing && analyzingResumeId === resume.id
                          }
                          aria-label="Analyze resume"
                        >
                          {analyzingResumeId === resume.id ? (
                            <ResumeAnalyzeIcon className="resume-action-icon analyzing-spinner" />
                          ) : (
                            <ResumeAnalyzeIcon className="resume-action-icon" />
                          )}
                        </button>
                      </PopupTooltip>
                      {(() => {
                        const status = processingStatus;
                        const isNotProcessed =
                          status !== "processed" && status !== "completed";
                        const reprocessTooltip = generatedResumeBusy
                          ? generatedResumeBusyMessage
                          : status === "processing"
                            ? "Retry ATS processing"
                            : status === "pending" || status === "uploaded"
                              ? "Start ATS processing for job-app parsing"
                              : "Reprocess ATS analysis";
                        return isNotProcessed ? (
                          <PopupTooltip
                            content={reprocessTooltip}
                            placement="top"
                          >
                            <button
                              className={`action-button reprocess-button ${reprocessingResumeId === resume.id ? "reprocessing" : ""} ${generatedResumeBusy ? "is-disabled" : ""}`}
                              onClick={(e) => {
                                if (guardBusyResumeAction(e)) return;
                                handleReprocessResume(e, resume);
                              }}
                              title={reprocessTooltip}
                              data-tour-id="resume-reprocess"
                              aria-disabled={
                                generatedResumeBusy ||
                                reprocessingResumeId === resume.id
                              }
                              disabled={reprocessingResumeId === resume.id}
                              aria-label={reprocessTooltip}
                            >
                              {reprocessingResumeId === resume.id ? (
                                <ResumeReprocessIcon className="resume-action-icon reprocessing-spinner" />
                              ) : (
                                <ResumeReprocessIcon className="resume-action-icon" />
                              )}
                            </button>
                          </PopupTooltip>
                        ) : null;
                      })()}
                      <PopupTooltip
                        content={
                          generatedResumeBusy
                            ? generatedResumeBusyMessage
                            : "Remove this resume from your account. You need at least one resume for applications and tailored resumes."
                        }
                        placement="top"
                      >
                        <button
                          className={`action-button delete-button ${generatedResumeBusy ? "is-disabled" : ""}`}
                          onClick={(e) => {
                            if (guardBusyResumeAction(e)) return;
                            handleDeleteClick(e, resume.id);
                          }}
                          title="Delete resume"
                          data-tour-id="resume-delete"
                          aria-disabled={generatedResumeBusy}
                          aria-label="Delete resume"
                        >
                          <ResumeDeleteIcon className="resume-action-icon" />
                        </button>
                      </PopupTooltip>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div ref={pageContainerRef} className="resumes-page">
      <div className="page-header" data-tour-id="resumes-header">
        <div className="header-content">
          <h1 className="page-title">Resumes</h1>
          <p className="page-subtitle">Manage your resumes and CVs</p>
        </div>
        <div className="header-actions">
          <button
            className="add-resume-button"
            onClick={handleAddResume}
            title="Add a new resume"
            data-tour-id="upload-resume-button"
          >
            <span className="button-icon">➕</span>
            Add Resume
          </button>
        </div>
      </div>

      <div className="page-content" data-tour-id="resumes-list">
        {renderContent()}
      </div>

      {/* Resume Upload Modal */}
      {isUploadModalOpen && (
        <ResumeUploadModal
          open={isUploadModalOpen}
          onClose={handleCloseModal}
          resumesHook={resumesHook}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title">
                {deleteConfirmStep === 1
                  ? "Delete Resume"
                  : "Last Resume — Confirm Deletion"}
              </h3>
            </div>
            <div className="modal-body">
              {deleteConfirmStep === 1 ? (
                <p>
                  Are you sure you want to delete this resume? This action
                  cannot be undone.
                </p>
              ) : (
                <>
                  <p className="resume-delete-last-warning">
                    This is your only resume. If you delete it, you will have no
                    resumes in your account.
                  </p>
                  <p className="resume-delete-why-one">
                    At least one resume is required to use core features:
                    applying to jobs, generating tailored resumes, fit analysis,
                    and ATS parsing. Without a resume, you won&apos;t be able to
                    track applications or get job-specific recommendations.
                  </p>
                  <p>Do you still want to delete this resume?</p>
                </>
              )}
              {isDeleting && deletingResumeId === deleteConfirmId && (
                <div className="delete-loading">
                  <div className="loading-spinner small"></div>
                  <span>Deleting resume...</span>
                </div>
              )}
            </div>
            <div className="modal-footer">
              {deleteConfirmStep === 1 ? (
                <>
                  <button
                    className="btn btn-secondary"
                    onClick={handleDeleteCancel}
                    disabled={isDeleting}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={handleDeleteConfirm}
                    disabled={isDeleting}
                  >
                    {isDeleting && deletingResumeId === deleteConfirmId ? (
                      <>
                        <div className="loading-spinner small"></div>
                        Deleting...
                      </>
                    ) : (
                      "Delete"
                    )}
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="btn btn-secondary"
                    onClick={handleDeleteGoBack}
                    disabled={isDeleting}
                  >
                    Go back
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={handleDeleteConfirm}
                    disabled={isDeleting}
                  >
                    {isDeleting && deletingResumeId === deleteConfirmId ? (
                      <>
                        <div className="loading-spinner small"></div>
                        Deleting...
                      </>
                    ) : (
                      "Delete anyway"
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ATS Data Modal */}
      {isAtsModalOpen && selectedAtsResume && (
        <ATSDataViewer
          resume={selectedAtsResume}
          onClose={handleCloseAtsModal}
        />
      )}

      {/* Resume Analysis Slideout */}
      {isAnalysisModalOpen && analysis && (
        <>
          {/* Backdrop */}
          <div
            className={`resume-analysis-backdrop ${isAnalysisModalOpen ? "open" : ""}`}
            onClick={handleCloseAnalysisModal}
          />
          {/* Slideout Panel */}
          <div
            className={`resume-analysis-slideout ${isAnalysisModalOpen ? "open" : ""}`}
          >
            <div className="resume-analysis-slideout-header">
              <h2 className="resume-analysis-slideout-title">
                Resume Analysis Results
              </h2>
              <button
                className="resume-analysis-slideout-close"
                onClick={handleCloseAnalysisModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="resume-analysis-slideout-content">
              <ResumeAnalysisResults
                analysis={analysis}
                isLoading={isAnalyzing}
                error={analysisError}
                onGenerateResume={handleGenerateResumeFromSuggestions}
                isGeneratingResume={isGeneratingResume}
                onGenerateResumeAi={handleGenerateResumeFromSuggestionsAi}
                isGeneratingResumeAi={isGeneratingResumeAi}
                sourceFormat={getResumeGenerationSourceFormat(
                  analysisSourceResume,
                )}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};
